import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import enUS from 'date-fns/locale/en-US';
import { fr } from 'date-fns/locale';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, subMonths, addMonths } from 'date-fns';
import { useLanguage } from '@contexts/LanguageContext';
import { forceUtcToParisTime, convertParisTimeToUtc, formatDateInTimezone, getParisTimezoneCalendarFormats, createParisTimezoneLocalizer } from '@utils/dateUtils';
import { api } from '@services/api';
import './BorrowingCalendar.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Define all supported locales
const locales = {
    'en': enUS,
    'fr': fr,
};

// Create the Paris timezone localizer
const localizer = createParisTimezoneLocalizer(dateFnsLocalizer, locales);

/**
 * Calculate date range for calendar view to optimize API calls
 * Always fetches 1 month before and 1 month after current date for better coverage
 * @param {Date} date - Current calendar date
 * @param {string} view - Calendar view ('month', 'week', 'day', 'agenda')
 * @returns {Object} - Object with start and end dates in UTC for API calls
 */
const getCalendarDateRange = (date, view) => {
    if (!date) return null;

    // Always get 1 month before and 1 month after the current date
    // This ensures smooth navigation and better data coverage
    const start = startOfMonth(subMonths(date, 2));
    const end = endOfMonth(addMonths(date, 2));

    // Convert to UTC for API calls
    return {
        start: convertParisTimeToUtc(start),
        end: convertParisTimeToUtc(end)
    };
};

/**
 * BorrowingCalendar Component
 *
 * This component displays borrowings and reservations in a calendar view with timezone handling.
 * It manages its own data fetching based on contextModel prop.
 *
 * Timezone Behavior:
 * - The backend sends dates that represent UTC times but are formatted as local times
 * - This component automatically converts those times to Paris timezone (CEST/CET)
 * - Example: Backend sends "18:00" (meaning 18:00 UTC) → Displays as "20:00" (Paris summer time)
 *
 * Features:
 * - Multi-language support (EN/FR)
 * - Multiple views (month, week, day, agenda)
 * - Event type styling (reservation vs borrowing)
 * - Timezone indicator for user clarity
 * - Internal data fetching based on contextModel
 */
export const BorrowingCalendar = ({
    contextModel, // New prop: { type: 'resource', id: resourceId } or { type: 'user', id: userId }
    onSelectEvent,
    onSelectSlot,
    onNavigate, // External navigation callback
    onView, // External view change callback
    initialDate = new Date(), // Initial date for calendar
    initialView = 'month', // Initial view for calendar
    selectable = false,
    fieldTitle = 'resource',
    height = '500px',
    className = '',
    loading: externalLoading = false,
    error: externalError = null,
    ...calendarProps
}) => {
    const { t, currentLanguage } = useLanguage();
    const [calendarView, setCalendarView] = useState(initialView);
    const [calendarDate, setCalendarDate] = useState(initialDate);

    // Internal state for fetched data
    const [borrowings, setBorrowings] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState(null);

    // Debounce system to prevent excessive API calls
    const debounceTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const DEBOUNCE_DELAY = 300; // 300ms debounce delay

    // Debounced fetch function
    const debouncedFetchBorrowings = useCallback(async (contextModel, calendarDate, calendarView) => {
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Abort previous request if still pending
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        return new Promise((resolve) => {
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    // Create new abort controller for this request
                    abortControllerRef.current = new AbortController();

                    setInternalLoading(true);
                    setInternalError(null);

                    // Calculate date range for current view (2 months before and after)
                    const dateRange = getCalendarDateRange(calendarDate, calendarView);

                    // Prepare API parameters with date filtering
                    const params = { page_size: -1 }; // Get more records for better coverage
                    if (dateRange) {
                        params.start_date = dateRange.start.toISOString();
                        params.end_date = dateRange.end.toISOString();
                    }

                    let response;

                    if (!contextModel) {
                        // If no contextModel, fetch all borrowings
                        response = await api.borrowings.getAll(params);
                    } else if (!contextModel.id) {
                        setBorrowings([]);
                        resolve();
                        return;
                    } else {
                        switch (contextModel.model_type) {
                            case 'resource':
                                // For resources, use the resource-specific endpoint with date filtering
                                response = await api.resources.getBorrowings(contextModel.id, params);
                                break;
                            case 'user':
                                // For users, use the user borrowings endpoint with date filtering and include_all flag
                                params.include_all = true; // Get all borrowings, not just active ones
                                response = await api.users.getBorrowings(contextModel.id, params);
                                // The endpoint returns an array directly, wrap it in expected format
                                if (Array.isArray(response.data)) {
                                    response = { data: { models: response.data } };
                                }
                                break;
                            default:
                                throw new Error(`Unsupported contextModel type: ${contextModel.model_type}`);
                        }
                    }

                    // Check if request was aborted
                    if (abortControllerRef.current?.signal.aborted) {
                        return;
                    }

                    setBorrowings(response.data?.models || response.data || []);
                    resolve();
                } catch (error) {
                    // Don't set error if request was aborted
                    if (error.name !== 'AbortError') {
                        setInternalError(error.message || 'Failed to fetch borrowings');
                        setBorrowings([]);
                    }
                    resolve();
                } finally {
                    setInternalLoading(false);
                }
            }, DEBOUNCE_DELAY);
        });
    }, []);

    // Fetch borrowings based on contextModel
    useEffect(() => {
        debouncedFetchBorrowings(contextModel, calendarDate, calendarView);

        // Cleanup function to clear timeout and abort request on unmount
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [contextModel, calendarDate, calendarView, debouncedFetchBorrowings]); // Refetch on any calendar change (date or view)

    // Sync with external props
    useEffect(() => {
        setCalendarDate(initialDate);
    }, []);

    useEffect(() => {
        setCalendarView(initialView);
    }, []);

    // Get the current locale for date-fns
    const currentLocale = useMemo(() => {
        return locales[currentLanguage] || locales['en'];
    }, [currentLanguage]);

    const getEndBorrowingDate = (borrowing) => {
        if (borrowing.returned_at) return borrowing.returned_at;

        if (borrowing.status === 'overdue') {
            // return now
            return new Date();
        }
        return borrowing.expected_return_at || new Date();
    };

    // Convert events to calendar events
    const calendarEvents = useMemo(() => {
        // Use fetched borrowings data
        const sourceEvents = borrowings;

        if (!sourceEvents || sourceEvents.length === 0) return [];

        const allEvents = sourceEvents.map(event => {
            // Process borrowing data for calendar events
            const allBorrowingData = [];

            const userName = event.user?.name || event.user?.email || t('borrowingCalendar.unknownUser');

            // Get description field from dynamic_fields
            let descriptionText = '';
            if (event.dynamic_fields) {
                for (const [fieldKey, fieldData] of Object.entries(event.dynamic_fields)) {
                    const fieldValue = fieldData?.value !== undefined ? fieldData.value : fieldData;
                    const fieldDef = fieldData?.definition;

                    if (fieldDef?.description_field && fieldValue !== undefined) {
                        descriptionText = fieldValue;
                        break;
                    }
                }
            }

            const baseTitle = fieldTitle === 'user'
                ? userName
                : event.resource?.name;

            const title = descriptionText ? `${descriptionText} - ${baseTitle}` : baseTitle;
            // 1. Add reservation event if reservation dates exist
            if (event.reserved_from && event.reserved_until && !event.borrowed_at) {
                const reservationStart = forceUtcToParisTime(event.reserved_from) || new Date(event.reserved_from);
                const reservationEnd = forceUtcToParisTime(event.reserved_until) || new Date(event.reserved_until);

                // Determine reservation sub-status
                let reservationStatus = 'reserved';
                if (event.validation_status === 'pending') {
                    reservationStatus = 'pending_validation';
                } else if (event.validation_status === 'rejected') {
                    reservationStatus = 'validation_rejected';
                }

                allBorrowingData.push({
                    id: `${event.id}-reservation`,
                    title: title,
                    start: reservationStart,
                    end: reservationEnd,
                    borrowing: event,
                    type: 'reservation',
                    status: reservationStatus,
                    originalId: event.id,
                    allDay: false,
                });
            }

            // 2. Add actual borrowing event if borrowed
            if (event.borrowed_at) {
                const borrowingStart = forceUtcToParisTime(event.borrowed_at) || new Date(event.borrowed_at);
                const endDate = getEndBorrowingDate(event);
                const borrowingEnd = forceUtcToParisTime(endDate) || endDate;

                allBorrowingData.push({
                    id: `${event.id}-borrowing`,
                    title: title,
                    start: borrowingStart,
                    end: borrowingEnd,
                    borrowing: event,
                    type: 'borrowing',
                    status: event.returned_at ? 'returned' : 'active',
                    originalId: event.id,
                    allDay: false,
                });
            }

            return allBorrowingData;
        }).flat().filter(Boolean);

        return allEvents;
    }, [borrowings, t]);  // Custom formats based on current language with Paris timezone
    const calendarFormats = useMemo(() => {
        return getParisTimezoneCalendarFormats(currentLocale);
    }, [currentLocale]);

    // Handle calendar event selection
    const handleSelectEvent = useCallback((event) => {
        if (onSelectEvent) {
            onSelectEvent(event);
        }
    }, [onSelectEvent]);

    // Handle calendar slot selection (for creating new reservations)
    const handleSelectSlot = useCallback((slotInfo) => {
        if (onSelectSlot) {
            // Convert slot times from Paris timezone to UTC for the backend
            const utcSlot = {
                ...slotInfo,
                start: convertParisTimeToUtc(slotInfo.start),
                end: convertParisTimeToUtc(slotInfo.end)
            };
            onSelectSlot(utcSlot);
        }
    }, [onSelectSlot]);

    // Handle calendar navigation (next/previous/today buttons)
    const handleNavigate = useCallback((date) => {
        setCalendarDate(date);
        // Call external navigation callback if provided
        if (onNavigate) {
            onNavigate(date, calendarView);
        }
    }, [calendarView, onNavigate]);

    // Handle calendar view change (month/week/day/agenda)
    const handleViewChange = useCallback((view) => {
        setCalendarView(view);
        // Call external view change callback if provided
        if (onView) {
            onView(view);
        }
        // Also call navigate callback to ensure proper synchronization
        if (onNavigate) {
            onNavigate(calendarDate, view);
        }
    }, [calendarDate, onView, onNavigate]);

    // Custom event component for better styling
    const EventComponent = ({ event }) => {
        const tooltipText = event.type === 'borrowing'
            ? t('borrowingCalendar.viewBorrowingDetails')
            : t('borrowingCalendar.viewReservationDetails');

        return (
            <div
                className={`borrowing-event event-type-${event.type}-${event.status}`}
                title={tooltipText}
            >
                <strong>{event.title}</strong>
                {event.borrowing?.notes && (
                    <div className="event-notes">{event.borrowing.notes}</div>
                )}
                {event.borrowing?.reservation_notes && (
                    <div className="event-notes">{event.borrowing.reservation_notes}</div>
                )}
            </div>
        );
    };

    // Event style getter for different colors based on event type
    const eventStyleGetter = useCallback((event) => {
        // Use CSS classes instead of inline styles for better maintainability
        let className = 'custom-event';

        switch (event.type) {
            case 'reservation':
                className += ' event-type-reservation';
                if (event.status === 'pending_validation') {
                    className += ' event-status-pending-validation';
                } else if (event.status === 'validation_rejected') {
                    className += ' event-status-validation-rejected';
                }
                break;
            case 'borrowing':
                className += ' event-type-borrowing';
                // Différenciation des emprunts selon leur statut
                if (event.status === 'returned') {
                    className += ' event-status-returned';
                }
                break;
            default:
                className += ' event-type-default';
                break;
        }

        return {
            className
        };
    }, []);

    // Custom toolbar component for internationalization
    const CustomToolbar = ({ label, onNavigate, onView, view }) => (
        <div className="calendar-toolbar">
            <div className="toolbar-navigation">
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => onNavigate('TODAY')}
                >
                    {t('borrowingCalendar.today')}
                </button>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => onNavigate('PREV')}
                >
                    {t('borrowingCalendar.previous')}
                </button>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => onNavigate('NEXT')}
                >
                    {t('borrowingCalendar.next')}
                </button>
            </div>

            <div className="toolbar-label">
                <h4>{label}</h4>
            </div>

            <div className="toolbar-views">
                <div className="btn-group" role="group">
                    <button
                        className={`btn btn-sm ${view === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onView('month')}
                    >
                        {t('borrowingCalendar.month')}
                    </button>
                    <button
                        className={`btn btn-sm ${view === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onView('week')}
                    >
                        {t('borrowingCalendar.week')}
                    </button>
                    <button
                        className={`btn btn-sm ${view === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onView('day')}
                    >
                        {t('borrowingCalendar.day')}
                    </button>
                    <button
                        className={`btn btn-sm ${view === 'agenda' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => onView('agenda')}
                    >
                        {t('borrowingCalendar.agenda')}
                    </button>
                </div>
            </div>
        </div>
    );

    // Determine final loading and error states
    const isLoading = externalLoading || internalLoading;
    const errorMessage = externalError || internalError;

    // Loading state
    if (isLoading) {
        return (
            <div className={`borrowing-calendar-loading ${className}`}>
                <div className="loading-state" style={{ height }}>
                    <div className="loading-spinner"></div>
                    <p>{t('borrowingCalendar.loadingEvents')}</p>
                </div>
            </div>
        );
    }

    // Error state
    if (errorMessage) {
        return (
            <div className={`borrowing-calendar-error ${className}`}>
                <div className="error-state" style={{ height }}>
                    <div className="error-icon">⚠️</div>
                    <p>{t('borrowingCalendar.errorLoadingEvents')}</p>
                    <small>{errorMessage}</small>
                </div>
            </div>
        );
    }

    return (
        <div className={`borrowing-calendar ${className}`}>
            {/* Timezone indicator */}
            <div className="timezone-indicator" style={{
                fontSize: '0.8rem',
                color: '#666',
                textAlign: 'right',
                marginBottom: '0.5rem'
            }}>
                {t('borrowingCalendar.timezone', 'Heures affichées en heure de Paris (CEST/CET)')}
            </div>


            <div className="calendar-container" style={{ height }}>
                <Calendar
                    localizer={localizer}
                    culture={currentLanguage}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable={selectable}
                    views={['month', 'week', 'day', 'agenda']}
                    view={calendarView}
                    date={calendarDate}
                    onNavigate={handleNavigate}
                    onView={handleViewChange}
                    eventPropGetter={eventStyleGetter}
                    components={{
                        event: EventComponent,
                        toolbar: CustomToolbar,
                    }}
                    messages={{
                        next: t('borrowingCalendar.next'),
                        previous: t('borrowingCalendar.previous'),
                        today: t('borrowingCalendar.today'),
                        month: t('borrowingCalendar.month'),
                        week: t('borrowingCalendar.week'),
                        day: t('borrowingCalendar.day'),
                        agenda: t('borrowingCalendar.agenda'),
                        date: t('borrowingCalendar.date'),
                        time: t('borrowingCalendar.time'),
                        event: t('borrowingCalendar.event'),
                        allDay: t('borrowingCalendar.allDay'),
                        work_week: t('borrowingCalendar.work_week'),
                        yesterday: t('borrowingCalendar.yesterday'),
                        tomorrow: t('borrowingCalendar.tomorrow'),
                        noEventsInRange: t('borrowingCalendar.noEventsInRange'),
                        showMore: (total) => t('borrowingCalendar.showMore', { count: total }),
                    }}
                    formats={calendarFormats}
                    {...calendarProps}
                />
            </div>
        </div>
    );
};

export default BorrowingCalendar;
